import { Channel, MulticastChannel } from 'redux-saga';
import { call, fork, take, takeLatest } from 'typed-redux-saga';

import { ChannelHolder } from '../../../helpers/channelHolder';
import { Logger } from '../../../helpers/loggerFacade';
import { multicastTakeEvery } from '../../../helpers/redux-saga';
import { Connection } from '../Connection';
import { SetDescriptionEvent, SignalingAPI } from '../SignalingAPI';
import { Description, SDKAdapter } from '../types';

export function* connectionSaga({
    setDescriptionChannel,
    onOutgoingSdpChannel,
    signaling,
    adapter,
    connection,
    channelHolder,
    logger,
}: {
    setDescriptionChannel: Channel<SetDescriptionEvent>;
    onOutgoingSdpChannel: MulticastChannel<Description>;
    signaling: SignalingAPI;
    adapter: SDKAdapter;
    connection: Connection;
    channelHolder: ChannelHolder;
    logger: Logger;
}) {
    try {
        yield* channelHolder.takeLatest(connection.onConnected, function* () {
            logger.debug('handling connection connected signal');

            yield* call([signaling, signaling.established]);
        });

        yield* channelHolder.takeLatest(connection.onHalt, function* (descriptor) {
            try {
                yield* call(adapter.cancel);
                yield* take(adapter.disconnectDoneAction);
            } finally {
                yield* call([signaling, signaling.halt], descriptor);
            }
        });

        yield* multicastTakeEvery(onOutgoingSdpChannel, function* (description) {
            yield* call([signaling, signaling.sendDescription], description);
        });

        yield* fork(function* () {
            while (true) {
                const event = yield* take(setDescriptionChannel);

                try {
                    logger.debug('handling incoming description', event.data);

                    yield* call([connection, connection.connect], event.data);

                    event.resolve();
                } catch (error) {
                    logger.error(error, {
                        method: 'connectionSaga:signaling:setDescriptionEventChannel',
                    });
                    event.reject(error);
                }
            }
        });

        yield* takeLatest(signaling.terminateEventChannel, function* (event) {
            logger.debug('handling terminate signal');

            try {
                yield* call([connection, connection.close]);
                yield* call(adapter.cancel);
                yield* take(adapter.disconnectDoneAction);
            } catch (error) {
                logger.error(error, {
                    method: 'connectionSaga:signaling.terminateEventChannel',
                });
            } finally {
                event.resolve();
            }
        });

        logger.debug('rtc connection was created');
    } catch (error) {
        logger.error(error, {
            method: 'connectionSaga',
        });

        throw error;
    }
}
